import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { botService, blindboxService, websocketService, bilibiliBindingService } from '../services';
import BackButton from '../components/BackButton';
import './BlindBoxStats.css';

function BlindBoxStats() {
  const navigate = useNavigate();
  
  // 绑定状态检查
  const [accessStatus, setAccessStatus] = useState({ loading: true, hasAccess: false, reason: '' });
  
  // 房间相关
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 盲盒相关
  const [blindBoxList, setBlindBoxList] = useState([]);
  const [selectedBlindBox, setSelectedBlindBox] = useState(null);
  const [autoClearOnLive, setAutoClearOnLive] = useState(false);
  
  // 统计数据
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  
  // OBS 显示选项
  const [obsTransparent, setObsTransparent] = useState(true);
  const [obsShowTitle, setObsShowTitle] = useState(true);
  const [obsSize, setObsSize] = useState('medium');
  const [obsShowTotalCount, setObsShowTotalCount] = useState(false);
  const [obsShowProfit, setObsShowProfit] = useState(false);
  
  // OBS 访问令牌
  const [accessToken, setAccessToken] = useState(null);
  
  // WebSocket 连接状态
  const [wsConnected, setWsConnected] = useState(false);
  
  // 检查绑定状态
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const result = await bilibiliBindingService.checkFeatureAccess();
        setAccessStatus({
          loading: false,
          hasAccess: result.hasAccess,
          reason: result.reason || '',
          code: result.code || ''
        });
      } catch (err) {
        console.error('检查访问权限失败:', err);
        if (err.response?.status === 403) {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: err.response.data?.message || '您没有权限使用此功能',
            code: err.response.data?.code || ''
          });
        } else {
          setAccessStatus({
            loading: false,
            hasAccess: false,
            reason: '检查权限失败，请稍后重试'
          });
        }
      }
    };
    checkAccess();
  }, []);
  
  // 加载房间列表
  useEffect(() => {
    const loadRooms = async () => {
      try {
        setLoading(true);
        const config = await botService.getBotConfig();
        if (config && config.rooms) {
          const enabledRooms = config.rooms.filter(r => r.enabled);
          setRooms(enabledRooms);
          if (enabledRooms.length > 0) {
            // 使用 uid（主播UID）作为选中值，与 user_rooms 表存储保持一致
            const firstRoom = enabledRooms[0];
            setSelectedRoom(firstRoom.uid);
          }
        }
      } catch (err) {
        console.error('加载房间列表失败:', err);
        setError('加载房间列表失败');
      } finally {
        setLoading(false);
      }
    };
    
    loadRooms();
  }, []);
  
  // 加载盲盒列表
  useEffect(() => {
    const loadBlindBoxList = async () => {
      try {
        const list = await blindboxService.getBlindBoxList();
        setBlindBoxList(list || []);
      } catch (err) {
        console.error('加载盲盒列表失败:', err);
      }
    };
    
    loadBlindBoxList();
  }, []);
  
  // 加载配置和统计数据
  useEffect(() => {
    if (!selectedRoom) return;
    
    const loadData = async () => {
      try {
        setStatsLoading(true);
        
        // 加载配置
        const config = await blindboxService.getBlindBoxConfig(selectedRoom);
        setSelectedBlindBox(config.selected_blind_gift);
        setAutoClearOnLive(config.auto_clear_on_live);
        // 加载显示选项
        setObsTransparent(config.display_transparent !== false);
        setObsShowTitle(config.display_show_title !== false);
        setObsSize(config.display_size || 'medium');
        setObsShowTotalCount(config.display_show_total_count === true);
        setObsShowProfit(config.display_show_profit === true);
        
        // 加载统计
        const statsData = await blindboxService.getBlindBoxStats(selectedRoom);
        setStats(statsData || {});
        
        // 加载 access token
        try {
          const tokenData = await blindboxService.getAccessToken(selectedRoom);
          setAccessToken(tokenData.access_token);
        } catch (err) {
          console.error('加载访问令牌失败:', err);
          setAccessToken(null);
        }
      } catch (err) {
        console.error('加载数据失败:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    
    loadData();
  }, [selectedRoom]);
  
  // WebSocket 连接
  useEffect(() => {
    if (!selectedRoom) return;
    
    // 连接状态监听
    const handleConnected = () => setWsConnected(true);
    const handleDisconnected = () => setWsConnected(false);
    
    websocketService.on('connected', handleConnected);
    websocketService.on('disconnected', handleDisconnected);
    
    // 订阅房间
    websocketService.subscribeRoom(selectedRoom);
    
    // 检查当前连接状态（解决从其他页面进入时状态不正确的问题）
    setWsConnected(websocketService.isConnected());
    
    return () => {
      websocketService.off('connected', handleConnected);
      websocketService.off('disconnected', handleDisconnected);
      websocketService.unsubscribeRoom(selectedRoom);
    };
  }, [selectedRoom]);
  
  // 监听盲盒更新事件
  useEffect(() => {
    const handleBlindboxUpdate = (data) => {
      const updateData = data.data;
      if (String(updateData.room_id) !== String(selectedRoom)) return;

      setStats(prevStats => {
        const newStats = { ...prevStats };
        const boxName = updateData.blind_gift_name;

        if (!newStats[boxName]) {
          newStats[boxName] = { blind_gift_price: 0, gifts: [] };
        }

        // 查找是否已有该礼物
        const gifts = newStats[boxName].gifts || [];
        const existingIndex = gifts.findIndex(
          g => g.gift_id === updateData.gift_id
        );

        if (existingIndex >= 0) {
          // 更新已有礼物的数量
          newStats[boxName] = {
            ...newStats[boxName],
            gifts: [...gifts]
          };
          newStats[boxName].gifts[existingIndex] = {
            ...newStats[boxName].gifts[existingIndex],
            count: newStats[boxName].gifts[existingIndex].count + updateData.count
          };
        } else {
          // 添加新礼物
          newStats[boxName] = {
            ...newStats[boxName],
            gifts: [...gifts, {
              gift_id: updateData.gift_id,
              gift_name: updateData.gift_name,
              gift_price: updateData.gift_price,
              gift_img: updateData.gift_img,
              count: updateData.count
            }]
          };
          // 按 gift_id 从高到低排序
          newStats[boxName].gifts.sort((a, b) => b.gift_id - a.gift_id);
        }

        return newStats;
      });
    };

    const handleBlindboxCleared = (data) => {
      if (String(data.data?.room_id) !== String(selectedRoom)) return;
      setStats({});
    };
    
    websocketService.on('blindbox_update', handleBlindboxUpdate);
    websocketService.on('blindbox_cleared', handleBlindboxCleared);
    
    return () => {
      websocketService.off('blindbox_update', handleBlindboxUpdate);
      websocketService.off('blindbox_cleared', handleBlindboxCleared);
    };
  }, [selectedRoom]);
  
  // 选择盲盒
  const handleSelectBlindBox = useCallback(async (boxName) => {
    setSelectedBlindBox(boxName);
    
    if (selectedRoom) {
      try {
        await blindboxService.saveBlindBoxConfig(selectedRoom, {
          selected_blind_gift: boxName,
          auto_clear_on_live: autoClearOnLive,
          display_transparent: obsTransparent,
          display_show_title: obsShowTitle,
          display_size: obsSize,
          display_show_total_count: obsShowTotalCount,
          display_show_profit: obsShowProfit
        });
      } catch (err) {
        console.error('保存配置失败:', err);
      }
    }
  }, [selectedRoom, autoClearOnLive, obsTransparent, obsShowTitle, obsSize, obsShowTotalCount, obsShowProfit]);

  // 切换开播自动清空
  const handleToggleAutoClear = useCallback(async () => {
    const newValue = !autoClearOnLive;
    setAutoClearOnLive(newValue);

    if (selectedRoom) {
      try {
        await blindboxService.saveBlindBoxConfig(selectedRoom, {
          selected_blind_gift: selectedBlindBox,
          auto_clear_on_live: newValue,
          display_transparent: obsTransparent,
          display_show_title: obsShowTitle,
          display_size: obsSize,
          display_show_total_count: obsShowTotalCount,
          display_show_profit: obsShowProfit
        });
      } catch (err) {
        console.error('保存配置失败:', err);
      }
    }
  }, [selectedRoom, selectedBlindBox, obsTransparent, obsShowTitle, obsSize, obsShowTotalCount, obsShowProfit]);

  // 保存显示选项
  const saveDisplayOptions = useCallback(async (options) => {
    if (!selectedRoom) return;

    try {
      await blindboxService.saveBlindBoxConfig(selectedRoom, {
        selected_blind_gift: selectedBlindBox,
        auto_clear_on_live: autoClearOnLive,
        display_transparent: options.transparent ?? obsTransparent,
        display_show_title: options.showTitle ?? obsShowTitle,
        display_size: options.size ?? obsSize,
        display_show_total_count: options.showTotalCount ?? obsShowTotalCount,
        display_show_profit: options.showProfit ?? obsShowProfit
      });
    } catch (err) {
      console.error('保存显示选项失败:', err);
    }
  }, [selectedRoom, selectedBlindBox, autoClearOnLive, obsTransparent, obsShowTitle, obsSize, obsShowTotalCount, obsShowProfit]);

  // 处理显示选项变更
  const handleTransparentChange = useCallback((value) => {
    setObsTransparent(value);
    saveDisplayOptions({ transparent: value });
  }, [saveDisplayOptions]);

  const handleShowTitleChange = useCallback((value) => {
    setObsShowTitle(value);
    saveDisplayOptions({ showTitle: value });
  }, [saveDisplayOptions]);

  const handleSizeChange = useCallback((value) => {
    setObsSize(value);
    saveDisplayOptions({ size: value });
  }, [saveDisplayOptions]);

  const handleShowTotalCountChange = useCallback((value) => {
    setObsShowTotalCount(value);
    saveDisplayOptions({ showTotalCount: value });
  }, [saveDisplayOptions]);

  const handleShowProfitChange = useCallback((value) => {
    setObsShowProfit(value);
    saveDisplayOptions({ showProfit: value });
  }, [saveDisplayOptions]);
  
  // 清空统计
  const handleClearStats = useCallback(async () => {
    if (!selectedRoom) return;
    
    if (!window.confirm('确定要清空所有盲盒统计数据吗？')) return;
    
    try {
      await blindboxService.clearBlindBoxStats(selectedRoom);
      setStats({});
    } catch (err) {
      console.error('清空统计失败:', err);
      alert('清空统计失败');
    }
  }, [selectedRoom]);
  
  // 复制 OBS 源地址（使用 access_token 混淆）
  const handleCopyOBSUrl = useCallback(() => {
    if (!accessToken) {
      alert('正在获取访问令牌，请稍后重试');
      return;
    }
    
    const url = `${window.location.origin}/live/blindbox/${accessToken}`;
    
    navigator.clipboard.writeText(url).then(() => {
      alert('OBS 源地址已复制到剪贴板\n\n注意：此链接已加密，请勿泄露给他人');
    }).catch(err => {
      console.error('复制失败:', err);
      prompt('请手动复制 OBS 源地址:', url);
    });
  }, [accessToken]);

  // 重新生成 access_token
  const handleRegenerateToken = useCallback(async () => {
    if (!selectedRoom) return;
    
    if (!window.confirm('确定要重新生成访问令牌吗？\n\n注意：重新生成后，旧的 OBS 源地址将失效，需要重新配置 OBS 浏览器源。')) return;
    
    try {
      const result = await blindboxService.regenerateAccessToken(selectedRoom);
      setAccessToken(result.access_token);
      alert('访问令牌已重新生成');
    } catch (err) {
      console.error('重新生成令牌失败:', err);
      alert('重新生成令牌失败');
    }
  }, [selectedRoom]);

  // 获取当前选中盲盒的统计数据（适配新格式）
  const currentBoxData = selectedBlindBox ? (stats[selectedBlindBox] || { gifts: [] }) : { gifts: [] };
  const currentStats = currentBoxData.gifts || [];
  const blindGiftPrice = currentBoxData.blind_gift_price || 0;

  // 获取当前房间信息
  const currentRoom = rooms.find(r => String(r.uid) === String(selectedRoom));
  
  // 检查绑定状态 loading
  if (accessStatus.loading) {
    return (
      <div className="container">
        <div className="blindbox-stats-page">
          <p>检查权限...</p>
        </div>
      </div>
    );
  }
  
  // 没有访问权限，重定向到首页
  if (!accessStatus.hasAccess) {
    navigate('/');
    return null;
  }
  
  if (loading) {
    return (
      <div className="container">
        <div className="blindbox-stats-page">
          <p>加载中...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container">
        <div className="blindbox-stats-page">
          <BackButton />
          <p className="error">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container">
      <div className="blindbox-stats-page">
        <BackButton />
        
        <div className="page-header">
          <h1 className="page-title">📦 盲盒统计</h1>
          <Link to="/admin/gift-screenshot" className="link-btn">
            📸 礼物截图生成
          </Link>
        </div>
        
        <div className="main-layout">
          {/* 左侧预览区域 */}
          <div className="left-panel">
            <div className="section preview-section">
              <h2 className="section-title">OBS 预览</h2>
              <div className="preview-container">
                {selectedRoom && (
                  <iframe
                    key={`${obsTransparent}-${obsShowTitle}-${obsSize}`}
                    src={`/live/blindbox/${selectedRoom}?preview=1`}
                    title="OBS Preview"
                    className="preview-iframe"
                  />
                )}
              </div>
            </div>
            
            {/* 连接状态 */}
            <div className="connection-status">
              <span className={`status-dot ${wsConnected ? 'connected' : 'disconnected'}`}></span>
              {wsConnected ? '实时连接已建立' : '实时连接断开'}
            </div>
          </div>
          
          {/* 右侧配置区域 */}
          <div className="right-panel">
            {/* 房间选择 */}
            <div className="section">
              <label className="section-label">选择直播间</label>
              <select 
                className="room-select"
                value={selectedRoom || ''}
                onChange={(e) => setSelectedRoom(e.target.value)}
              >
                {rooms.map(room => {
                  const displayRoomId = room.anchor_info?.room_id || room.uid;
                  const roomName = room.anchor_info?.uname || `UID ${room.uid}`;
                  return (
                    <option key={room.uid} value={room.uid}>
                      {roomName} (房间号: {displayRoomId})
                    </option>
                  );
                })}
              </select>
            </div>
            
            {/* 盲盒选择 */}
            <div className="section">
              <label className="section-label">选择盲盒</label>
              <div className="blindbox-grid">
                {blindBoxList.map(box => (
                  <button
                    key={box.name}
                    className={`blindbox-btn ${selectedBlindBox === box.name ? 'selected' : ''}`}
                    onClick={() => handleSelectBlindBox(box.name)}
                  >
                    {box.name}
                  </button>
                ))}
                {blindBoxList.length === 0 && (
                  <p className="no-data">暂无盲盒数据，请先在直播间收到盲盒礼物</p>
                )}
              </div>
            </div>
            
            {/* OBS 显示选项 */}
            <div className="section obs-options">
              <label className="section-label">OBS 显示选项（自动保存）</label>

              <div className="options-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsTransparent}
                    onChange={(e) => handleTransparentChange(e.target.checked)}
                  />
                  透明背景
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsShowTitle}
                    onChange={(e) => handleShowTitleChange(e.target.checked)}
                  />
                  显示标题
                </label>

                <div className="size-selector">
                  <span>尺寸：</span>
                  <select
                    value={obsSize}
                    onChange={(e) => handleSizeChange(e.target.value)}
                    className="size-select"
                  >
                    <option value="small">小</option>
                    <option value="medium">中</option>
                    <option value="large">大</option>
                  </select>
                </div>
              </div>

              <div className="options-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsShowTotalCount}
                    onChange={(e) => handleShowTotalCountChange(e.target.checked)}
                  />
                  显示抽取总数
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={obsShowProfit}
                    onChange={(e) => handleShowProfitChange(e.target.checked)}
                  />
                  显示盈亏
                </label>
              </div>

              <div className="obs-url-actions">
                <button className="btn btn-secondary" onClick={handleCopyOBSUrl}>
                  复制 OBS 源地址
                </button>
                <button className="btn btn-outline" onClick={handleRegenerateToken} title="重新生成后旧链接将失效">
                  🔄 重新生成链接
                </button>
              </div>
              
              <p className="obs-url-hint">
                💡 OBS 链接已加密，不会直接暴露房间号
              </p>
            </div>
            
            {/* 控制按钮 */}
            <div className="section controls">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={autoClearOnLive}
                  onChange={handleToggleAutoClear}
                />
                开播自动清空统计
              </label>
              
              <button className="btn btn-danger" onClick={handleClearStats}>
                清空所有统计
              </button>
            </div>
            
            {/* 统计展示 */}
            <div className="section">
              <h2 className="section-title">
                {selectedBlindBox ? `${selectedBlindBox} 礼物统计` : '请选择盲盒'}
              </h2>
              
              {statsLoading ? (
                <p>加载统计数据...</p>
              ) : selectedBlindBox ? (
                <div className="stats-list">
                  {currentStats.length > 0 ? (
                    currentStats.map((gift, index) => (
                      <div key={gift.gift_id || index} className="gift-item">
                        {gift.gift_img && (
                          <img 
                            src={gift.gift_img} 
                            alt={gift.gift_name} 
                            className="gift-icon"
                            referrerPolicy="no-referrer"
                          />
                        )}
                        <span className="gift-name">{gift.gift_name}</span>
                        <span className="gift-price">{gift.gift_price / 100} 电池</span>
                        <span className="gift-count">x{gift.count}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">暂无统计数据</p>
                  )}
                </div>
              ) : (
                <p className="no-data">请先选择一个盲盒</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlindBoxStats;
